import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import { Clock, Plus, Download, FileText, Pencil, Lock, Zap } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { ContractAddendumPopup } from '../components/ContractAddendumPopup';

interface HoursRegistration {
  id: string;
  employer_id: string;
  dossier_id?: string;
  datum: string;
  tijd_minuten: number;
  type: string;
  omschrijving?: string;
  aantal_km?: number;
  reistijd_uren?: number;
  parkeerkosten?: number;
  onkosten_locatie?: number;
  afhuren_locatie?: number;
  invoiced?: boolean;
}

export const ProfessionalHoursPage: React.FC = () => {
  const { t } = useTranslation('professional');
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingRegistration, setEditingRegistration] = useState<HoursRegistration | null>(null);
  const [selectedEmployer, setSelectedEmployer] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const queryClient = useQueryClient();

  // Get active employers
  const { data: employers, error: employersError, isLoading: employersLoading } = useQuery<any[], Error>({
    queryKey: ['active-employers'],
    queryFn: async (): Promise<any[]> => {
      console.log('[DEBUG] Starting fetch for active-employers');
      try {
        const response = await api.get('/professional/active-employers');
        console.log('[DEBUG] API response:', {
          status: response.status,
          success: response.data?.success,
          dataLength: response.data?.data?.length,
          data: response.data?.data,
          fullResponse: response.data
        });
        const result = response.data.data || [];
        console.log('[DEBUG] Returning employers array:', {
          length: result.length,
          isArray: Array.isArray(result),
          sample: result[0] || null
        });
        return result;
      } catch (err: any) {
        console.error('[DEBUG] Error fetching employers:', {
          message: err?.message,
          status: err?.response?.status,
          responseData: err?.response?.data
        });
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...({ cacheTime: 10 * 60 * 1000 } as any) // 10 minutes
  });

  // Get hours registrations
  const { data: registrations, isLoading } = useQuery({
    queryKey: ['hours-registrations', selectedEmployer, selectedMonth, selectedYear],
    queryFn: async () => {
      const params: any = {};
      if (selectedEmployer) params.employer_id = selectedEmployer;
      if (selectedMonth) params.month = selectedMonth;
      if (selectedYear) params.year = selectedYear;

      const response = await api.get('/professional/hours', { params });
      return response.data.data || [];
    }
  });

  // Get dossiers for selected employer
  const { data: dossiers } = useQuery<any[], Error>({
    queryKey: ['dossiers', selectedEmployer],
    queryFn: async (): Promise<any[]> => {
      if (!selectedEmployer) return [];
      const response = await api.get('/professional/dossiers', {
        params: { employer_id: selectedEmployer }
      });
      return response.data.data || [];
    },
    enabled: !!selectedEmployer
  });

  const createRegistration = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/professional/hours', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('toast.hoursRegistered'));
      queryClient.invalidateQueries({ queryKey: ['hours-registrations'] });
      setShowForm(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('toast.hoursRegisterError'));
    }
  });

  const updateRegistration = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await api.put(`/professional/hours/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('toast.hoursUpdated', 'Urenregistratie bijgewerkt'));
      queryClient.invalidateQueries({ queryKey: ['hours-registrations'] });
      setEditingRegistration(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('toast.hoursUpdateError', 'Fout bij bijwerken'));
    }
  });

  const handleExport = async () => {
    if (!registrations || registrations.length === 0) {
      toast.error(t('hours.noRegistrationsExport'));
      return;
    }

    try {
      const params: any = {
        month: selectedMonth,
        year: selectedYear
      };

      if (selectedEmployer) {
        params.employer_id = selectedEmployer;
      }

      const response = await api.get('/professional/hours/export', { params });
      const exportData = response.data.data;

      // Get employer name if selected
      const selectedEmployerData = employers?.find((e: any) => e.employer_user_id === selectedEmployer);
      const employerName = selectedEmployerData
        ? `${selectedEmployerData.employer_first_name} ${selectedEmployerData.employer_last_name}`
        : t('hours.allEmployers');

      // Create CSV content
      const monthNames = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
                          'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];
      const monthName = monthNames[selectedMonth - 1];

      let csvContent = `${t('hours.exportTitle')} - ${monthName} ${selectedYear}\n`;
      csvContent += `${t('hours.employer')}: ${employerName}\n`;
      csvContent += `\n`;

      // Headers
      csvContent += `${t('hours.csvDate')},${t('hours.csvType')},${t('hours.csvTimeMinutes')},${t('hours.csvTimeHours')},${t('hours.csvDescription')},${t('hours.csvKm')},${t('hours.csvParking')},${t('hours.csvLocationExpenses')},${t('hours.csvLocationRental')},${t('hours.csvHourlyRate')},${t('hours.csvTotalAmount')}\n`;

      // Data rows
      exportData.registrations.forEach((reg: any) => {
        const datum = new Date(reg.datum).toLocaleDateString('nl-NL');
        const uren = (reg.tijd_minuten || 0) / 60;
        const typeLabels: Record<string, string> = {
          'intake_op_locatie': t('hourTypes.intake_locatie'),
          'bezoek_op_locatie': t('hourTypes.bezoek_locatie'),
          'online_intake': t('hourTypes.online_intake'),
          'online_gesprek': t('hourTypes.online_gesprek'),
          'communicatie': t('hourTypes.communicatie'),
          'administratie': t('hourTypes.administratie'),
          'overige': t('hourTypes.overige')
        };
        const typeLabel = typeLabels[reg.type] || reg.type;
        const omschrijving = (reg.beschrijving || reg.omschrijving || '').replace(/"/g, '""');

        csvContent += `"${datum}","${typeLabel}",${reg.tijd_minuten || 0},${uren.toFixed(2)},"${omschrijving}",${reg.aantal_km || 0},${reg.parkeerkosten || 0},${reg.onkosten_locatie || 0},${reg.afhuren_locatie || 0},\u20AC${reg.hourly_rate || 0},\u20AC${reg.total_amount || 0}\n`;
      });

      // Totals
      csvContent += `\n`;
      csvContent += `${t('hours.totalMinutes')},${exportData.totals.total_minutes}\n`;
      csvContent += `${t('hours.totalHours')},${exportData.totals.total_hours.toFixed(2)}\n`;
      csvContent += `${t('hours.totalAmount')},\u20AC${exportData.totals.total_amount.toFixed(2)}\n`;
      csvContent += `${t('hours.totalKm')},${exportData.totals.total_km}\n`;
      csvContent += `${t('hours.totalParking')},\u20AC${exportData.totals.total_parkeerkosten.toFixed(2)}\n`;
      csvContent += `${t('hours.totalExpenses')},\u20AC${exportData.totals.total_onkosten.toFixed(2)}\n`;

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `urenregistratie_${monthName}_${selectedYear}_${selectedEmployer ? employerName.replace(/\s+/g, '_') : 'alle'}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(t('hours.exportedDownloaded'));
    } catch (error: any) {
      console.error('Export error:', error);
      toast.error(error?.response?.data?.message || t('hours.exportError'));
    }
  };

  const totalMinutes = registrations?.reduce((sum: number, r: HoursRegistration) => sum + (r.tijd_minuten || 0), 0) || 0;
  const totalHours = totalMinutes / 60;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <ContractAddendumPopup context="uren" />
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('sidebar.hoursRegistration')}</h1>
          <p className="mt-1 text-gray-600">{t('hours.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/professional/invoices')}
          >
            <FileText className="w-4 h-4 mr-2" />
            {t('hours.createInvoices')}
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={!registrations || registrations.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            {t('hours.export')}
          </Button>
          <Button variant="outline" onClick={() => navigate('/professional/adhoc-uren')}>
            <Zap className="w-4 h-4 mr-2" />
            Ad-hoc uren
          </Button>
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('hours.newRegistration')}
          </Button>
        </div>
      </div>

      {/* Debug info - remove after fixing */}
      {(import.meta as any).env?.DEV && (
        <Card className="mb-4 p-4 bg-yellow-50">
          <div className="text-xs">
            <strong>Debug Info:</strong>
            <div>Loading: {employersLoading ? 'Yes' : 'No'}</div>
            <div>Error: {employersError ? employersError.message : 'None'}</div>
            <div>Employers defined: {employers !== undefined ? 'Yes' : 'No'}</div>
            <div>Employers length: {employers?.length || 0}</div>
            <div>Sample employer: {JSON.stringify(employers?.[0] || null)}</div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('hours.employer')}
            </label>
            {employersLoading ? (
              <div className="text-sm text-gray-500">{t('hours.loadingText')}</div>
            ) : employersError ? (
              <div className="text-sm text-red-500">{t('hours.loadError')}: {employersError.message}</div>
            ) : (
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                value={selectedEmployer}
                onChange={(e) => {
                  setSelectedEmployer(e.target.value);
                }}
              >
                <option value="">{t('hours.allEmployers')}</option>
                {employers?.map((emp: any) => {
                  const employerId = emp.employer_user_id || emp.employer_id;
                  const firstName = emp.employer_first_name || emp.first_name || '';
                  const lastName = emp.employer_last_name || emp.last_name || '';
                  if (!employerId) {
                    console.warn('[DEBUG] Employer missing ID:', emp);
                    return null;
                  }
                  return (
                    <option key={employerId} value={employerId}>
                      {firstName} {lastName}
                    </option>
                  );
                })}
              </select>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('hours.month')}
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('hours.year')}
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <div className="text-sm">
              <div className="font-medium">{t('hours.totalLabel')}: {totalHours.toFixed(2)} {t('hours.hoursUnit')}</div>
              <div className="text-gray-500">{totalMinutes} {t('hours.minutesUnit')}</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Registration Form (create) */}
      {showForm && (
        <HoursRegistrationForm
          employers={employers || []}
          dossiers={dossiers || []}
          onSubmit={(data) => createRegistration.mutate(data)}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Edit Form */}
      {editingRegistration && (
        <HoursRegistrationForm
          employers={employers || []}
          dossiers={dossiers || []}
          initialData={editingRegistration}
          isEditing
          onSubmit={(data) => updateRegistration.mutate({ id: editingRegistration.id, data })}
          onCancel={() => setEditingRegistration(null)}
        />
      )}

      {/* Registrations List */}
      <Card>
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">{t('hours.loadingText')}</p>
          </div>
        ) : registrations && registrations.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('table.date')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('hours.type')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('hours.time')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('hours.description')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('hours.expenses')}</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {registrations.map((reg: HoursRegistration) => (
                  <tr key={reg.id} className={editingRegistration?.id === reg.id ? 'bg-blue-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{new Date(reg.datum).toLocaleDateString('nl-NL')}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{t(`hourTypes.${reg.type === 'intake_op_locatie' ? 'intake_locatie' : reg.type === 'bezoek_op_locatie' ? 'bezoek_locatie' : reg.type}`, reg.type)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{reg.tijd_minuten} min</td>
                    <td className="px-6 py-4 text-sm">{reg.omschrijving || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {reg.aantal_km && `KM: ${reg.aantal_km}, `}
                      {reg.parkeerkosten && `${t('hours.parkingShort')}: \u20AC${reg.parkeerkosten}, `}
                      {reg.onkosten_locatie && `${t('hours.expensesShort')}: \u20AC${reg.onkosten_locatie}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      {reg.invoiced ? (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                          <Lock className="w-3 h-3" /> {t('hours.invoiced', 'Gefactureerd')}
                        </span>
                      ) : (
                        <button
                          onClick={() => { setEditingRegistration(reg); setShowForm(false); }}
                          className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-xs"
                        >
                          <Pencil className="w-3 h-3" /> {t('hours.edit', 'Wijzigen')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Clock className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('hours.noRegistrations')}
            </h2>
            <p className="text-gray-600">
              {t('hours.noRegistrationsDesc')}
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};

// Hours Registration Form Component
const HoursRegistrationForm: React.FC<{
  employers: any[];
  dossiers: any[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
  initialData?: HoursRegistration;
  isEditing?: boolean;
}> = ({ employers, dossiers, onSubmit, onCancel, initialData, isEditing }) => {
  const { t } = useTranslation('professional');
  const [formData, setFormData] = useState({
    employer_id: initialData?.employer_id || '',
    dossier_id: initialData?.dossier_id || '',
    datum: initialData ? new Date(initialData.datum).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    tijd_minuten: initialData?.tijd_minuten?.toString() || '',
    type: initialData?.type || 'overige',
    omschrijving: initialData?.omschrijving || '',
    aantal_km: initialData?.aantal_km?.toString() || '',
    reistijd_uren: initialData?.reistijd_uren?.toString() || '',
    parkeerkosten: initialData?.parkeerkosten?.toString() || '',
    parking_receipt_file_path: '',
    onkosten_locatie: initialData?.onkosten_locatie?.toString() || '',
    afhuren_locatie: initialData?.afhuren_locatie?.toString() || ''
  });

  // Expense fields are now always available for all types

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      tijd_minuten: parseInt(formData.tijd_minuten),
      omschrijving: formData.omschrijving || undefined, // Make sure omschrijving is included
      aantal_km: formData.aantal_km ? parseFloat(formData.aantal_km) : undefined,
      reistijd_uren: formData.reistijd_uren ? parseFloat(formData.reistijd_uren) : undefined,
      parkeerkosten: formData.parkeerkosten ? parseFloat(formData.parkeerkosten) : undefined,
      parking_receipt_file_path: formData.parking_receipt_file_path || undefined,
      onkosten_locatie: formData.onkosten_locatie ? parseFloat(formData.onkosten_locatie) : undefined,
      afhuren_locatie: formData.afhuren_locatie ? parseFloat(formData.afhuren_locatie) : undefined,
      dossier_id: formData.dossier_id || undefined
    });
  };

  const filteredDossiers = dossiers.filter((d: any) => d.employer_id === formData.employer_id);

  return (
    <Card className="mb-6" padding={false}>
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        {isEditing && (
          <div className="flex items-center gap-2 pb-2 border-b">
            <Pencil className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-600">{t('hours.editingRegistration', 'Registratie wijzigen')}</span>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('form.employerRequired')}
            </label>
            <select
              required
              disabled={isEditing}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              value={formData.employer_id}
              onChange={(e) => setFormData({ ...formData, employer_id: e.target.value, dossier_id: '' })}
            >
              <option value="">{t('form.selectEmployer')}</option>
              {employers.map((emp: any) => (
                <option key={emp.employer_user_id} value={emp.employer_user_id}>
                  {emp.employer_first_name} {emp.employer_last_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('form.dateRequired')}
            </label>
            <input
              type="date"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.datum}
              onChange={(e) => setFormData({ ...formData, datum: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('form.dossierOptional')}
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.dossier_id}
              onChange={(e) => setFormData({ ...formData, dossier_id: e.target.value })}
            >
              <option value="">{t('form.other')}</option>
              {filteredDossiers.map((d: any) => (
                <option key={d.id} value={d.id}>{d.client_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('form.typeRequired')}
            </label>
            <select
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            >
              {[
                { value: 'intake_op_locatie', labelKey: 'hourTypes.intake_locatie' },
                { value: 'bezoek_op_locatie', labelKey: 'hourTypes.bezoek_locatie' },
                { value: 'online_intake', labelKey: 'hourTypes.online_intake' },
                { value: 'online_gesprek', labelKey: 'hourTypes.online_gesprek' },
                { value: 'communicatie', labelKey: 'hourTypes.communicatie' },
                { value: 'administratie', labelKey: 'hourTypes.administratie' },
                { value: 'overige', labelKey: 'hourTypes.overige' }
              ].map(type => (
                <option key={type.value} value={type.value}>{t(type.labelKey)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('form.timeMinutes')}
            </label>
            <input
              type="number"
              required
              min="1"
              placeholder={t('form.timePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.tijd_minuten}
              onChange={(e) => setFormData({ ...formData, tijd_minuten: e.target.value })}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('form.descriptionOptional')}
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={2}
              placeholder={t('form.descriptionPlaceholder')}
              value={formData.omschrijving}
              onChange={(e) => setFormData({ ...formData, omschrijving: e.target.value })}
            />
          </div>
        </div>

        {/* Expense fields */}
        <div className="border-t pt-4 mt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-4">{t('form.expenses')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.kmDriven')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={formData.aantal_km}
                  onChange={(e) => setFormData({ ...formData, aantal_km: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.travelTime')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={formData.reistijd_uren}
                  onChange={(e) => setFormData({ ...formData, reistijd_uren: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.parkingCosts')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={formData.parkeerkosten}
                  onChange={(e) => setFormData({ ...formData, parkeerkosten: e.target.value })}
                />
                <label className="block text-sm font-medium text-gray-700 mb-2 mt-2">
                  {t('form.parkingReceipt')}
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      try {
                        const formData = new FormData();
                        formData.append('receipt', file);
                        const response = await api.post('/uploads/parking-receipt', formData, {
                          headers: {
                            'Content-Type': 'multipart/form-data'
                          }
                        });
                        if (response.data.success) {
                          setFormData(prev => ({ ...prev, parking_receipt_file_path: response.data.file_path }));
                          toast.success(t('toast.receiptUploaded'));
                        }
                      } catch (error: any) {
                        toast.error(error?.response?.data?.error || t('toast.receiptUploadError'));
                      }
                    }
                  }}
                />
                {formData.parking_receipt_file_path && (
                  <p className="text-sm text-green-600 mt-1">{t('cockpit.receiptUploaded')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.locationExpenses')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={formData.onkosten_locatie}
                  onChange={(e) => setFormData({ ...formData, onkosten_locatie: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.locationRental')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={formData.afhuren_locatie}
                  onChange={(e) => setFormData({ ...formData, afhuren_locatie: e.target.value })}
                />
              </div>
            </div>
          </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('hours.cancel')}
          </Button>
          <Button type="submit">
            {isEditing ? t('hours.saveChanges', 'Wijzigingen opslaan') : t('hours.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
};
